import {useMemo} from 'react';

import {useRunsData, useRunSetsQuery} from '../../../state/runs/hooks';
import * as FilterActions from '../../../state/views/filter/actions';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelActions from '../../../state/views/panel/actions';
import * as PanelTypes from '../../../state/views/panel/types';
import {transformQuery} from '../../../util/panelTypes';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {usePanelWithInheritedSettings} from '../../WorkspaceDrawer/Settings/hooks/usePanelInheritedSettings';
import {usePanelContext} from '../PanelContextProvider';

export const usePanelSharedProps = (panelRef: PanelTypes.Ref) => {
  const {customRunColorsRef, customRunNamesRef, runSetRefs, semanticLegendRef} =
    usePanelsSharedRefsContext();

  const {inheritedSettings, panelBankSectionConfigRef} = usePanelContext();

  const panel = usePanelWithInheritedSettings(panelRef, inheritedSettings);

  const updateConfig = ViewHooks.useViewAction(
    panelRef,
    (
      ref,
      configUpdate: Partial<PanelTypes.Panel['config']>,
      options?: {isUndoable?: boolean}
    ) =>
      PanelActions.updateConfig(
        ref,
        configUpdate,
        panelBankSectionConfigRef,
        options
      )
  );

  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const semanticLegendSettings = ViewHooks.usePartMaybe(semanticLegendRef);

  const runSet = ViewHooks.usePart(runSetRefs[0]);
  const convertSelectionsToFilters = ViewHooks.useViewAction(
    runSet.filtersRef,
    FilterActions.selectionsToFilters
  );

  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const panelQuery = useMemo(
    () => transformQuery(query, panel),
    [query, panel]
  );

  const runsDataQuery = useRunsData(panelQuery);

  return {
    convertSelectionsToFilters,
    customRunColors,
    customRunNames,
    data: runsDataQuery.data,
    loading: runsDataQuery.loading,
    pageQuery: query,
    query: panelQuery,
    semanticLegendSettings,
    updateConfig,
    runsDataQuery,
    config: panel.config,
  };
};
